import { Injectable } from '@angular/core';

import { PublicProjectRelease, ReleaseWithReadState } from './public-release-feed.model';

/** localStorage key holding the set of already-seen release versions. */
const STORAGE_KEY = 'pwd.releaseNotes.readVersions';

/**
 * Per-browser read state for the public release feed. Since the feed is a public
 * static file (no backend, no per-user row), "read/unread" is tracked locally in
 * `localStorage` as a set of release `version` strings the user has seen.
 *
 * Every access is defensive: a missing / corrupt / quota-blocked `localStorage`
 * degrades to "nothing read" and never throws into the UI.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesStore {
  private read(): Set<string> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === 'string')) : new Set();
    } catch {
      return new Set();
    }
  }

  private write(versions: Set<string>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...versions]));
    } catch {
      /* private mode / quota — read state is best-effort */
    }
  }

  isRead(version: string): boolean {
    return this.read().has(version);
  }

  markRead(version: string): void {
    const set = this.read();
    if (set.has(version)) return;
    set.add(version);
    this.write(set);
  }

  markAllRead(versions: string[]): void {
    const set = this.read();
    versions.forEach((v) => set.add(v));
    this.write(set);
  }

  /** Enrich releases with `is_read` from the local set. */
  withReadState(releases: PublicProjectRelease[]): ReleaseWithReadState[] {
    const set = this.read();
    return releases.map((r) => ({ ...r, is_read: set.has(r.version) }));
  }
}
